import type { Settings } from '@il-colpo/shared/settings';
import type { Levels } from './engine.ts';
import type { Scene } from './scenes.ts';
import { nativeBuzz } from '../native/native.ts';
import { IN_CAPACITOR } from '../native/shell.ts';
import { hapticFor, type SfxKey } from './sfx.ts';

/**
 * What the game talks to when it wants a sound. The synthesiser behind it is Tone.js, which is
 * big and needs a user gesture, so it is loaded the first time the player touches the page and
 * not before. Until then (and in tests, and where Web Audio does not exist) every call is a
 * harmless no-op.
 */

/** The synthesiser, as the manager needs it. `engine.ts` is the real one. */
export interface AudioBackend {
  setLevels: (levels: Levels) => void;
  setScene: (scene: Scene) => void;
  setAlarm: (alarm: number) => void;
  setSeed: (seed: string) => void;
  sfx: (key: SfxKey, velocity?: number, delay?: number) => void;
  suspend: () => void;
  resume: () => void;
  dispose: () => void;
}

export type BackendLoader = () => Promise<AudioBackend>;

const INTERACTIVE =
  'button, a[href], [role="button"], [role="radio"], [role="tab"], [role="switch"], select';

/** The real loader: the engine and Tone.js arrive in a chunk of their own. */
const defaultLoader: BackendLoader = async () => {
  const { AudioEngine } = await import('./engine.ts');
  return AudioEngine.create();
};

export class AudioManager {
  private backend: AudioBackend | null = null;
  private loading: Promise<void> | null = null;
  private installed = false;
  private levels: Levels = { master: 0.8, music: 0.7, sfx: 0.9 };
  private vibration = true;
  private scene: Scene = 'off';
  private alarm = 0;
  private seed = 'menu';
  private hovered: Element | null = null;

  constructor(
    private readonly load: BackendLoader = defaultLoader,
    private readonly supported: () => boolean = () => typeof AudioContext !== 'undefined',
  ) {}

  /** Wires the page: the first touch unlocks the sound, hidden tabs go quiet, buttons click. */
  install(target: Window = window): () => void {
    if (this.installed) return () => undefined;
    this.installed = true;
    const unlock = (): void => {
      void this.unlock();
    };
    const onDown = (e: Event): void => {
      unlock();
      const el = (e.target instanceof Element ? e.target : null)?.closest(INTERACTIVE) ?? null;
      if (el && !(el as HTMLButtonElement).disabled) this.sfx('ui.click');
      else if (el) this.sfx('ui.error');
    };
    const onOver = (e: PointerEvent): void => {
      if (e.pointerType !== 'mouse') return;
      const el = (e.target instanceof Element ? e.target : null)?.closest(INTERACTIVE) ?? null;
      if (el === this.hovered) return;
      this.hovered = el;
      if (el && !(el as HTMLButtonElement).disabled) this.sfx('ui.hover');
    };
    const onKey = (e: KeyboardEvent): void => {
      unlock();
      if ((e.key === 'Enter' || e.key === ' ') && e.target instanceof Element) {
        if (e.target.closest(INTERACTIVE)) this.sfx('ui.click');
      }
    };
    const onVisibility = (): void => {
      if (document.hidden) this.backend?.suspend();
      else this.backend?.resume();
    };
    target.addEventListener('pointerdown', onDown, { capture: true });
    target.addEventListener('pointerover', onOver, { passive: true });
    target.addEventListener('keydown', onKey, { capture: true });
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      this.installed = false;
      target.removeEventListener('pointerdown', onDown, { capture: true });
      target.removeEventListener('pointerover', onOver);
      target.removeEventListener('keydown', onKey, { capture: true });
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }

  /** Loads and starts the synthesiser. Safe to call again and again. */
  unlock(): Promise<void> {
    if (this.backend) return Promise.resolve();
    if (!this.supported()) return Promise.resolve();
    // With every slider at zero there is nothing to play: do not load anything yet.
    if (this.levels.master <= 0) return Promise.resolve();
    this.loading ??= this.load()
      .then((backend) => {
        this.backend = backend;
        backend.setLevels(this.levels);
        backend.setSeed(this.seed);
        backend.setAlarm(this.alarm);
        backend.setScene(this.scene);
      })
      .catch((error: unknown) => {
        // No sound is better than a broken page.
        console.warn('Audio unavailable.', error);
      })
      .finally(() => {
        this.loading = null;
      });
    return this.loading;
  }

  applySettings(settings: Settings): void {
    this.levels = {
      master: settings.audio.master,
      music: settings.audio.music,
      sfx: settings.audio.sfx,
    };
    this.vibration = settings.accessibility.vibration;
    this.backend?.setLevels(this.levels);
  }

  setScene(scene: Scene): void {
    this.scene = scene;
    this.backend?.setScene(scene);
  }

  setAlarm(alarm: number): void {
    this.alarm = alarm;
    this.backend?.setAlarm(alarm);
  }

  /** The seed that composes the score. Never the game's own seed: that one is secret. */
  setSeed(seed: string): void {
    this.seed = seed;
    this.backend?.setSeed(seed);
  }

  /** One effect, with a buzz on devices that have one. */
  sfx(key: SfxKey, velocity = 1): void {
    this.buzz(key);
    this.backend?.sfx(key, velocity);
  }

  private buzz(key: SfxKey): void {
    if (!this.vibration) return;
    const pattern = hapticFor(key);
    if (pattern.length === 0) return;
    // A native app has the device's haptics (iOS has no `navigator.vibrate` at all).
    if (IN_CAPACITOR) nativeBuzz(pattern);
    else if (typeof navigator !== 'undefined' && 'vibrate' in navigator)
      navigator.vibrate([...pattern]);
  }

  /** For tests and tools: what the manager is doing now. */
  state(): { loaded: boolean; scene: Scene; alarm: number; levels: Levels } {
    return {
      loaded: this.backend !== null,
      scene: this.scene,
      alarm: this.alarm,
      levels: this.levels,
    };
  }

  dispose(): void {
    this.backend?.dispose();
    this.backend = null;
  }
}

export const audio = new AudioManager();
